/* oxlint-disable react/no-unstable-nested-components */
import { Table } from 'antd';
import classNames from 'classnames';
import { t } from 'ttag';
import { NextIcon2, PrevIcon2 } from './assets';
import type { TableComponentProps } from './types';
import { useTableComponent } from './useTableComponent';
import { showingCountPage } from './tableComponent.handler';
import './style.scss';

/**
 * Placeholder row handler standing in for the table's onRow callback. Takes no arguments and returns undefined.
 *
 * @returns Undefined.
 */
const NOOP = (): any => undefined;

/**
 * Paginated Ant Design table backing the study, device and ticket lists. Substitutes the skeletonData from useTableComponent while isLoading, configures pagination with 10, 30 and 50 page sizes, image-based previous and next controls and a total extrapolated one page past the current one until isEndOfData, highlights the row matching selectedRowKey, forwards row clicks to onRow and renders showingCountPage as the footer.
 *
 * @param props - Row data and columns, current page and page size, totals and ticket count, row key and selection, loading and end-of-data flags, and the table change and row handlers.
 * @returns The rendered table element.
 */
const TableComponent = (props: TableComponentProps) => {
  const {
    className = undefined,
    rowKey = 'id',
    rowSelection = undefined,
    onRow = NOOP,
    selectedRowKey = undefined,
    total = undefined,
    ticketCount = 0,
    isLoading = false,
    scrollX = undefined,
  } = props;
  const { tableRef, skeletonData } = useTableComponent(props);

  return (
    <div ref={tableRef}>
      <Table
        className={classNames('custom-table-component', className)}
        rowSelection={rowSelection}
        onChange={props.handleTableChange}
        rowKey={rowKey}
        dataSource={isLoading ? skeletonData : props.data}
        columns={props.columns}
        showSorterTooltip={false}
        scroll={{
          x: scrollX || true,
        }}
        loading={
          isLoading
            ? {
                indicator: <span />,
              }
            : undefined
        }
        pagination={{
          current: props.currentPage,
          pageSize: props.pageSize,
          pageSizeOptions: [10, 30, 50],
          showSizeChanger: true,
          locale: {
            items_per_page: 'per page',
          },
          itemRender: (page, type, originalElement) => {
            if (type === 'prev') {
              return <img src={PrevIcon2} alt="Next Icon" />;
            }
            if (type === 'next') {
              return <img src={NextIcon2} alt="Next Icon" />;
            }
            return originalElement;
          },
          className: 'custom-pagination',
          'aria-label': t`Pagination`,
          total: props.isEndOfData
            ? props.data.length
            : (props.currentPage ?? 1) * (props.pageSize ?? 0) + (props.pageSize ?? 0),
        }}
        onRow={(record, index) => ({
          onClick: () => {
            onRow(record, index);
          },
        })}
        rowClassName={(record) => (record[rowKey] === selectedRowKey ? 'selected-row' : '')}
        footer={() =>
          showingCountPage({
            data: props.data,
            currentPage: props.currentPage,
            pageSize: props.pageSize,
            ticketCount,
            total,
          })
        }
      />
    </div>
  );
};

export default TableComponent;
